import { useMemo } from "react";

import { sessionStorageGetItem } from "src/utils/session-storage";

import { AuthContext } from "src/services/auth/auth.types";
import { useCheckAuthStatusQuery } from "src/services/auth/auth.service";
import { TakeoverState, TAKEOVER_STORAGE_KEY } from "src/services/system/system.types";

interface IAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  context: AuthContext | undefined;
  refreshAuthState: () => void;
}

export const useAuth = (): IAuthResult => {

  const { currentData: data, isError, isLoading, refetch } = useCheckAuthStatusQuery(undefined);

  // Check for takeover state and modify auth context accordingly
  const contextWithTakeover = useMemo(() => {
    if (!data) return data;

    const takeoverData = sessionStorageGetItem(TAKEOVER_STORAGE_KEY);
    if (takeoverData) {
      try {
        const takeoverState: TakeoverState = JSON.parse(takeoverData);

        // Return modified auth context with takeover account/tenant info
        return {
          ...data,
          account: {
            ...data.account,
            id: takeoverState.account.id,
            organization: {
              ...data.account.organization,
              id: takeoverState.tenant.id,
              company_name: takeoverState.tenant.company_name,
              type: takeoverState.tenant.type,
            }
          },
          organization: {
            ...data.organization,
            id: takeoverState.tenant.id,
            type: takeoverState.tenant.type,
          },
          user: {
            ...data.user,
            id: takeoverState.account.id,
            email: takeoverState.account.email,
            first_name: takeoverState.account.first_name,
            last_name: takeoverState.account.last_name,
            name: `${takeoverState.account.first_name} ${takeoverState.account.last_name}`,
          }
        } as AuthContext;
      } catch (error) {
        console.error('Error parsing takeover state:', error);
        return data;
      }
    }

    return data;
  }, [data]);

  return {
    refreshAuthState: refetch,
    isAuthenticated: data !== undefined,
    isLoading,
    context: contextWithTakeover,
  };
};
